import type { ChangeEvent, FormEvent } from "react";
import React from "react";
import type { TFunction } from "i18next";
import { FaExchangeAlt, FaHome, FaSave, FaTimes, FaUserFriends } from "react-icons/fa";
import { FAMILY_SITUATIONS, familySituationLabel } from "../../../utils/familySituation";
import type { FamilyFormErrors, FamilyFormValues, ParentOption, ParentSlot } from "../types";
import { filterParentOptions, parentDisplayName } from "../utils/familyFormMapping";

interface FamilyFormProps {
  formId?: string;
  hideActions?: boolean;
  mode: "create" | "edit";
  values: FamilyFormValues;
  errors: FamilyFormErrors;
  saving?: boolean;
  parents: ParentOption[];
  onFamilyNameChange: (value: string) => void;
  onFamilySituationChange: (value: string) => void;
  onParentSearchChange: (slot: ParentSlot, value: string) => void;
  onSelectParent: (slot: ParentSlot, parent: ParentOption | null) => void;
  onSwapParents?: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
  t: TFunction;
}

const inputClass =
  "mt-2 h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-950 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-200 dark:border-white/10 dark:bg-slate-950/60 dark:text-white dark:focus:border-indigo-300";
const labelClass = "text-xs font-semibold text-slate-500 dark:text-slate-300";
const errorClass = "mt-1 text-xs font-semibold text-red-600 dark:text-red-300";

const errorMessage = (code: string | undefined, t: TFunction): string | null => {
  if (!code) return null;
  if (code === "required") return t("family.form.field_required", { defaultValue: "This field is required." });
  return code;
};

const slotLabel = (slot: ParentSlot, t: TFunction): string =>
  slot === "parent1"
    ? t("family.form.parent_one", { defaultValue: "Parent 1" })
    : t("family.form.parent_two", { defaultValue: "Parent 2" });

export default function FamilyForm({
  formId,
  hideActions = false,
  mode,
  values,
  errors,
  saving = false,
  parents,
  onFamilyNameChange,
  onFamilySituationChange,
  onParentSearchChange,
  onSelectParent,
  onSwapParents,
  onSubmit,
  onCancel,
  t,
}: FamilyFormProps) {
  const renderParentPicker = (slot: ParentSlot) => {
    const selected = values.selectedParents[slot];
    const search = values.search[slot];
    const message = errorMessage(errors[slot], t);
    const options = filterParentOptions(parents, search);

    return (
      <div className="relative overflow-visible rounded-[18px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/10">
        <label className={labelClass}>
          {slotLabel(slot, t)}
          {slot === "parent1" ? " *" : ""}
        </label>
        <input
          type="search"
          value={search}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onParentSearchChange(slot, event.target.value)}
          className={`${inputClass} ${message ? "border-red-400 focus:border-red-400 focus:ring-red-200" : ""}`}
          placeholder={t("family.new.search_parent", { defaultValue: "Search parent" })}
          aria-invalid={Boolean(message)}
        />
        {message && <p className={errorClass}>{message}</p>}

        {search && (
          <ul className="absolute z-50 mt-2 max-h-44 w-[calc(100%-2.5rem)] overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-white/10 dark:bg-slate-900">
            {options.length > 0 ? (
              options.map((parent) => (
                <li key={String(parent.id)}>
                  <button
                    type="button"
                    className="block w-full px-4 py-2 text-left text-sm font-semibold text-slate-700 transition hover:bg-indigo-50 hover:text-indigo-700 dark:text-slate-100 dark:hover:bg-white/10 dark:hover:text-white"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onSelectParent(slot, parent)}
                  >
                    {parentDisplayName(parent) || t("common.unknown", { defaultValue: "Unknown" })}
                  </button>
                </li>
              ))
            ) : (
              <li className="px-4 py-3 text-sm font-semibold text-slate-500 dark:text-slate-300">
                {t("family.form.no_parent_results", { defaultValue: "No parents found" })}
              </li>
            )}
          </ul>
        )}

        {selected && (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-bold text-indigo-800 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-100">
            <span className="min-w-0 truncate">{parentDisplayName(selected) || t("common.unknown", { defaultValue: "Unknown" })}</span>
            <button
              type="button"
              onClick={() => onSelectParent(slot, null)}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-red-600 transition hover:bg-red-50 dark:text-red-200 dark:hover:bg-red-400/10"
              aria-label={t("common.actions.remove", { defaultValue: "Remove" })}
            >
              <FaTimes aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    );
  };

  const familyNameError = errorMessage(errors.familyName, t);
  const familySituationError = errorMessage(errors.familySituation, t);
  const canSwap = Boolean(values.selectedParents.parent1 || values.selectedParents.parent2);

  return (
    <form
      id={formId}
      onSubmit={onSubmit}
      className="rounded-[24px] border border-slate-200/80 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/5 sm:p-5"
    >
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.85fr)]">
        <section className="rounded-[18px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/10">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-200">
              <FaHome aria-hidden="true" />
            </span>
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              {t("family.edit.general_information", { defaultValue: "General information" })}
            </h2>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className={labelClass}>
                {t("family.new.family_name", { defaultValue: "Family name" })} *
              </label>
              <input
                type="text"
                value={values.familyName}
                onChange={(event) => onFamilyNameChange(event.target.value)}
                className={`${inputClass} ${familyNameError ? "border-red-400 focus:border-red-400 focus:ring-red-200" : ""}`}
                placeholder={t("family.new.auto_generated", { defaultValue: "Generated from parent name" })}
                aria-invalid={Boolean(familyNameError)}
              />
              {familyNameError && <p className={errorClass}>{familyNameError}</p>}
            </div>

            <div>
              <label className={labelClass}>
                {t("family.new.family_situation", { defaultValue: "Family situation" })} *
              </label>
              <select
                value={values.familySituation}
                onChange={(event) => onFamilySituationChange(event.target.value)}
                className={`${inputClass} ${familySituationError ? "border-red-400 focus:border-red-400 focus:ring-red-200" : ""}`}
                aria-invalid={Boolean(familySituationError)}
              >
                {FAMILY_SITUATIONS.map((value) => (
                  <option key={value} value={value}>
                    {familySituationLabel(t, value)}
                  </option>
                ))}
              </select>
              {familySituationError && <p className={errorClass}>{familySituationError}</p>}
            </div>
          </div>
        </section>

        <section className="rounded-[18px] border border-indigo-100 bg-gradient-to-r from-indigo-50/80 via-white to-sky-50/60 p-5 shadow-sm dark:border-indigo-400/25 dark:from-indigo-950/25 dark:via-white/5 dark:to-sky-950/20">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 dark:bg-indigo-400/15 dark:text-indigo-200">
              <FaUserFriends aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="font-bold text-slate-950 dark:text-white">
                {t("common.next_step", { defaultValue: "Next step" })}
              </p>
              <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {mode === "create"
                  ? t("family.new.next_step", { defaultValue: "Link at least one parent and create the family." })
                  : t("family.edit.next_step", { defaultValue: "Review family composition and save the changes." })}
              </p>
            </div>
          </div>
        </section>
      </div>

      <section className="mt-5 rounded-[18px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/10">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-50 text-sky-600 dark:bg-sky-400/15 dark:text-sky-200">
              <FaUserFriends aria-hidden="true" />
            </span>
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              {t("family.details.parents", { defaultValue: "Parents" })}
            </h2>
          </div>
          {mode === "edit" && onSwapParents && (
            <button
              type="button"
              onClick={onSwapParents}
              disabled={!canSwap}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-4 py-2 text-xs font-bold text-indigo-700 transition hover:border-indigo-300 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-indigo-400/25 dark:bg-indigo-400/10 dark:text-indigo-200"
            >
              <FaExchangeAlt aria-hidden="true" />
              {t("family.edit.swap_parents", { defaultValue: "Swap parents" })}
            </button>
          )}
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          {renderParentPicker("parent1")}
          {renderParentPicker("parent2")}
        </div>
      </section>

      {!hideActions && (
        <div className="mt-6 flex flex-col-reverse gap-3 border-t border-slate-200/80 pt-5 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-slate-100 dark:hover:text-indigo-200"
          >
            <FaTimes aria-hidden="true" />
            {t("common.actions.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="submit"
            className="lumen-glow-sheen inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200/70 bg-indigo-50/80 px-6 py-2.5 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:hover:translate-y-0 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200 dark:hover:text-white"
            disabled={saving}
          >
            <FaSave aria-hidden="true" />
            {saving
              ? t("common.loading", { defaultValue: "Loading..." })
              : mode === "create"
                ? t("family.new.create", { defaultValue: "Create family" })
                : t("family.edit.update", { defaultValue: "Update family" })}
          </button>
        </div>
      )}
    </form>
  );
}
